import React, { useState, useEffect } from 'react';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import { toast } from 'sonner';
import LPHeader from '../components/LPHeader';
import LPFooter from '../components/LPFooter';
import InquiryModal from '../components/InquiryModal';
import LPHeroSection from '../components/lp/LPHeroSection';
import LPWhyYouNeedSection from '../components/lp/LPWhyYouNeedSection';
import LPHowItWorksSection from '../components/lp/LPHowItWorksSection';
import LPPricingSection from '../components/lp/LPPricingSection';
import LPWhyChooseUsSection from '../components/lp/LPWhyChooseUsSection';
import LPCTASection from '../components/lp/LPCTASection';
import LPTestimonialsSlider from '../components/lp/LPTestimonialsSlider';
import LPFAQSection from '../components/lp/LPFAQSection';
import BeforeAfterSection from '../components/BeforeAfterSection';

const LandingPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', brandName: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState('');

  const allCountryCodes = React.useMemo(() => {
    const codes = require('../data/countryCodes').default;
    return codes;
  }, []);

  useEffect(() => {
    const fetchGeo = async () => {
      try {
        const info = await getCountryInfo();
        if (info) {
          const match = allCountryCodes.find(c => c.country === info.countryCode);
          if (match) setCountryCode(match.code);
        }
      } catch (e) { console.error('Geo fetch error:', e); }
    };
    fetchGeo();
  }, [allCountryCodes]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => phone.replace(/[^\d]/g, '').length === 10;

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9\s\-]/g, '');
    const digits = value.replace(/[^\d]/g, '');
    if (digits.length > 10) return;
    setFormData({...formData, phone: value});
    if (errors.phone && digits.length === 10) setErrors(prev => ({ ...prev, phone: '' }));
  };

  const handleEmailChange = (e) => {
    setFormData({...formData, email: e.target.value});
    if (errors.email && validateEmail(e.target.value)) setErrors(prev => ({ ...prev, email: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.brandName) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please check the agreement box to continue');
      return;
    }

    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email (e.g., user@example.com)';
      hasErrors = true;
    }
    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits (e.g., 123-456-7890)';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsLoading(true);
    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        countryCode: countryCode,
        country: allCountryCodes.find(c => c.code === countryCode)?.country || 'US',
        description: `Trademark Application Hub: ${formData.brandName || 'Not provided'}`
      };

      const result = await submitLeadWithGeoLocation(leadData, null, 'Landing Page Form');
      
      if (result.success) {
        toast.success(result.message || 'Inquiry submitted! We\'ll contact you soon.');
        setFormData({ name: '', email: '', phone: '', brandName: '' });
      } else {
        toast.error(result.message || 'Failed to submit. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again later.');
      setIsLoading(false);
    }
  };

  const handleOpenModal = (context) => {
    setModalContext(context);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <LPHeader onOpenModal={handleOpenModal} />
      <LPHeroSection
        formData={formData}
        setFormData={setFormData}
        countryCode={countryCode}
        setCountryCode={setCountryCode}
        errors={errors}
        setErrors={setErrors}
        agreedToTerms={agreedToTerms}
        setAgreedToTerms={setAgreedToTerms}
        isLoading={isLoading}
        handleSubmit={handleSubmit}
        handleEmailChange={handleEmailChange}
        handlePhoneChange={handlePhoneChange}
        allCountryCodes={allCountryCodes}
      />
      <LPWhyYouNeedSection onOpenModal={handleOpenModal} />
      <BeforeAfterSection />
      <LPHowItWorksSection onOpenModal={handleOpenModal} />
      <LPPricingSection onOpenModal={handleOpenModal} />
      <LPWhyChooseUsSection onOpenModal={handleOpenModal} />
      <LPCTASection onOpenModal={handleOpenModal} />
      <LPTestimonialsSlider />
      <LPFAQSection />
      <LPFooter />
      <InquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonContext={modalContext} />
    </div>
  );
};

export default LandingPage;
